import { useCallback, useEffect, useRef, useState } from "react";
import { MondayApiQueryInstance } from "../../services/monday/monday-service-ts";
import { getMergeCandidates } from "../../utils/requests/itemMergeRequests";
import logger from "../../lib/logger";
import type { MergeEligibility } from "./mergeItem.types";

/** One picker page; also the server's cap on candidates per eligibility call. */
export const CANDIDATE_PAGE_SIZE = 50;

export interface MergeCandidate {
  id: string;
  name: string;
  eligibility?: MergeEligibility;
}

interface Args {
  token: string | null;
  currentItemId: string;
  currentBoardId: string;
  boardId: string | null;
  groupId: string | null;
  searchTerm: string;
}

/**
 * The chosen group's items, one page at a time, each with whether it can be merged.
 *
 * Items come from monday with the user's own SDK session (so only what they can see), eligibility
 * from the server's database check. A row whose eligibility call failed stays selectable: the
 * review step runs the full check anyway, and greying everything out on a transient error would
 * block the user for no reason.
 */
export const useMergeCandidates = ({ token, currentItemId, currentBoardId, boardId, groupId, searchTerm }: Args) => {
  const [items, setItems] = useState<MergeCandidate[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped whenever the query changes, so a slow page for an old query is discarded.
  const generation = useRef(0);

  const withEligibility = useCallback(
    async (page: Array<{ id: string; name: string }>): Promise<MergeCandidate[]> => {
      if (!token || !boardId || page.length === 0) return page;
      try {
        const { eligibility } = await getMergeCandidates(token, {
          currentItemId,
          currentBoardId,
          candidateBoardId: boardId,
          candidateItemIds: page.map((item) => String(item.id)),
        });
        return page.map((item) => ({ ...item, id: String(item.id), eligibility: eligibility[String(item.id)] }));
      } catch (err: any) {
        logger.error("Merge: eligibility check failed", err);
        return page.map((item) => ({ ...item, id: String(item.id) }));
      }
    },
    [token, boardId, currentItemId, currentBoardId],
  );

  useEffect(() => {
    const run = ++generation.current;
    setItems([]);
    setCursor(null);
    setError(null);
    if (!boardId || !groupId) return;

    setIsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const page = await MondayApiQueryInstance.getGroupItemsPage(
          Number(boardId),
          groupId,
          CANDIDATE_PAGE_SIZE,
          searchTerm,
        );
        const judged = await withEligibility(page.items);
        if (run !== generation.current) return;
        setItems(judged);
        setCursor(page.cursor);
      } catch (err: any) {
        if (run !== generation.current) return;
        logger.error("Merge: could not load the group's items", err);
        setError("Could not load this group's items.");
      } finally {
        if (run === generation.current) setIsLoading(false);
      }
    }, searchTerm ? 300 : 0);

    return () => clearTimeout(timer);
  }, [boardId, groupId, searchTerm, withEligibility]);

  const loadMore = useCallback(async () => {
    if (!cursor || isLoading) return;
    const run = generation.current;
    setIsLoading(true);
    try {
      const page = await MondayApiQueryInstance.getNextGroupItemsPage(cursor, CANDIDATE_PAGE_SIZE);
      const judged = await withEligibility(page.items);
      if (run !== generation.current) return;
      setItems((previous) => [...previous, ...judged]);
      setCursor(page.cursor);
    } catch (err: any) {
      if (run !== generation.current) return;
      logger.error("Merge: could not load more items", err);
      setError("Could not load more items.");
    } finally {
      if (run === generation.current) setIsLoading(false);
    }
  }, [cursor, isLoading, withEligibility]);

  return { items, isLoading, error, hasMore: Boolean(cursor), loadMore };
};
